'use client';

import { AlertCircle, Undo2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useLocale } from '@/components/common/locale-provider';
import { GenericPageSkeleton } from '@/components/layout/generic-page-skeleton';
import { SidebarToggle } from '@/components/sidebar/sidebar-toggle';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useArtifactSplitLayout } from '@/hooks/use-artifact-split-layout';
import { useTranslations } from '@/hooks/use-translations';

/**
 * Universal page container with configurable max width
 * Use this to wrap all page content for consistent spacing
 */
export const PageContainer = ({
  children,
  maxWidth = '6xl',
  className = '',
}: {
  children: React.ReactNode;
  maxWidth?: '2xl' | '4xl' | '6xl' | '7xl' | 'full';
  className?: string;
}) => {
  const widthClasses: Record<string, string> = {
    '2xl': 'max-w-2xl',
    '4xl': 'max-w-4xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
  };

  if (maxWidth === 'full') {
    return <div className={`w-full p-4 sm:p-6 ${className}`}>{children}</div>;
  }
  return (
    <div
      className={`container mx-auto ${widthClasses[maxWidth]} p-4 sm:p-6 ${className}`}
    >
      {children}
    </div>
  );
};

/**
 * Reusable back button with tooltip
 * Supports both router.back() and specific href navigation
 */
export const BackButton = ({
  href,
  onClick,
  label,
  className = 'ml-auto',
}: {
  href?: string;
  onClick?: () => void;
  label?: string;
  className?: string;
}) => {
  const router = useRouter();
  const { locale } = useLocale();
  const { t } = useTranslations(locale);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (href) {
      router.push(href);
    } else {
      router.back();
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          onClick={handleClick}
          className={`px-2 h-9 ${className}`}
          aria-label={label || t('common.back')}
        >
          <Undo2 className="size-4" aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent align="start">{label || t('common.back')}</TooltipContent>
    </Tooltip>
  );
};

/**
 * Universal page header with optional back button and actions
 * Replaces individual header implementations across pages
 */
interface PageHeaderProps {
  title: React.ReactNode;
  showBackButton?: boolean;
  backButtonHref?: string;
  backButtonLabel?: string;
  onBack?: () => void;
  actions?: React.ReactNode;
}

export const PageHeader = ({
  title,
  showBackButton = false,
  backButtonHref,
  backButtonLabel,
  onBack,
  actions,
}: PageHeaderProps) => (
  <header className="flex sticky top-0 bg-transparent items-center gap-2 px-2 py-1.5 z-10">
    <SidebarToggle />
    <h1 className="text-sm">{title}</h1>
    {actions}
    {showBackButton && (
      <BackButton
        href={backButtonHref}
        onClick={onBack}
        label={backButtonLabel}
      />
    )}
  </header>
);

export function ArtifactAwarePageHeader(props: PageHeaderProps) {
  const { isActive } = useArtifactSplitLayout();
  return isActive ? null : <PageHeader {...props} />;
}

/**
 * Standardized page title with icon and subtitle
 * Used below PageHeader for consistent page titles across admin and other pages
 */
export const PageTitle = ({
  icon: Icon,
  title,
  subtitle,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle?: string;
}) => (
  <div className="mb-4 sm:mb-6">
    <div className="flex items-center gap-2.5">
      {Icon && <Icon className="size-5 sm:size-6 text-primary shrink-0" />}
      <div>
        <h1 className="text-lg sm:text-xl font-semibold">{title}</h1>
        {subtitle && (
          <p className="text-xs sm:text-sm text-muted-foreground">{subtitle}</p>
        )}
      </div>
    </div>
  </div>
);

/**
 * Full page loading state with header
 * Provides consistent loading experience across all pages
 */
export const LoadingPage = ({
  title,
  message,
  showBackButton = false,
  backButtonHref,
  onBack,
  skeleton,
}: {
  title: string;
  message: string;
  showBackButton?: boolean;
  backButtonHref?: string;
  onBack?: () => void;
  skeleton?: React.ReactNode;
}) => (
  <>
    <PageHeader
      title={title}
      showBackButton={showBackButton}
      backButtonHref={backButtonHref}
      onBack={onBack}
    />
    <PageContainer>
      <div role="status" aria-live="polite" aria-label={message}>
        {skeleton || <GenericPageSkeleton />}
      </div>
    </PageContainer>
  </>
);

/**
 * Full page error state with retry option
 * Provides consistent error handling across all pages
 */
export const ErrorPage = ({
  title,
  message,
  showBackButton = false,
  backButtonHref,
  onBack,
  onRetry,
  retryLabel,
}: {
  title: string;
  message: string;
  showBackButton?: boolean;
  backButtonHref?: string;
  onBack?: () => void;
  onRetry?: () => void;
  retryLabel?: string;
}) => {
  const { locale } = useLocale();
  const { t } = useTranslations(locale);

  return (
    <>
      <PageHeader
        title={title}
        showBackButton={showBackButton}
        backButtonHref={backButtonHref}
        onBack={onBack}
      />
      <PageContainer maxWidth="2xl">
        <Alert variant="destructive" className="mx-auto">
          <AlertCircle className="size-4" />
          <AlertDescription>{message}</AlertDescription>
        </Alert>
        {onRetry && (
          <div className="text-center mt-6">
            <Button onClick={onRetry} variant="outline">
              {retryLabel || t('common.tryAgain')}
            </Button>
          </div>
        )}
      </PageContainer>
    </>
  );
};

/**
 * Empty state component for pages with no data
 * Provides consistent empty state experience
 */
export const EmptyState = ({
  icon: Icon,
  message,
  actionLabel,
  isActionDisabled,
  onAction,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  message: string;
  actionLabel?: string;
  isActionDisabled?: boolean;
  onAction?: () => void;
}) => (
  <div className="text-center py-12">
    {Icon && <Icon className="size-12 text-muted-foreground mx-auto mb-4" />}
    <p className="text-muted-foreground mb-4">{message}</p>
    {onAction && actionLabel && (
      <Button onClick={onAction} variant="outline" disabled={isActionDisabled}>
        {actionLabel}
      </Button>
    )}
  </div>
);
